import { useReadOnly } from "../../hooks/useReadOnly";
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import NavBar from '../../components/common/NavBar';
import { useProductScope, productPath, scopeTitle, scopeName } from '../../contexts/ProductScope';
import AreaPicker, { AreaIdBadges } from '../../components/areas/AreaPicker';
import type { RowExtras, RowState } from '../../components/areas/AreaPicker';
import { toSelectedArea } from '../../utils/odmSelection';
import { realtimeApi } from '../../services/realtimeApi';
import { projectsApi, Project } from '../../services/projectsApi';
import { projectInThisEnv } from '../../utils/realtimeEnv';
import { useRealtimeAreas } from '../../hooks/realtime/useRealtimeAreas';
import type { RealtimeArea } from '../../hooks/realtime/useRealtimeAreas';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import ProjectMonitorCards from '../../components/monitoring/ProjectMonitorCards';
import SharedProjectsWarning from '../../components/monitoring/SharedProjectsWarning';
import {
  monitorableMembers,
  sharedMembers,
  bulkSetMonitoring,
  SharedMember,
} from '../../services/projectMonitoring';

type SavedArea = RealtimeArea;

const pluralAreas = (n: number) => `${n} area${n === 1 ? '' : 's'}`;

const MonitorAreasSelect: React.FC = () => {
  const navigate = useNavigate();
  const scope = useProductScope();
  // Read-only (support view or a viewer): monitoring and projects can be seen, not changed.
  const { readOnly } = useReadOnly();
  const {
    savedAreas,
    loading,
    error,
    monitoringStates,
    setMonitoringStates,
    dataCountFor,
    monitoredAreas,
    otherAreas,
    hiddenNote,
  } = useRealtimeAreas();
  const [togglingStates, setTogglingStates] = useState<Record<string, boolean>>({});
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [savingProject, setSavingProject] = useState(false);
  const [projects, setProjects] = useState<Project[]>([]);
  const [nameModalOpen, setNameModalOpen] = useState(false);
  const [projectName, setProjectName] = useState('');
  const [monitorBusy, setMonitorBusy] = useState(false);
  const [confirmDisable, setConfirmDisable] = useState<{ project: Project; shared: SharedMember[] } | null>(null);
  // "Saved to <project>" after a save, with a link into it; clears itself.
  const [notice, setNotice] = useState<{ text: string; projectId: string } | null>(null);

  useEffect(() => {
    if (!notice) return;
    const t = setTimeout(() => setNotice(null), 8000);
    return () => clearTimeout(t);
  }, [notice]);

  // Saved projects (named groups of areas). localStorage today — see
  // src/services/projectsApi.ts and docs/projects-persistence.md.
  const refreshProjects = useCallback(async () => {
    try {
      setProjects(await projectsApi.list());
    } catch (err) {
      console.warn('Could not load projects:', err);
    }
  }, []);

  useEffect(() => {
    refreshProjects();
  }, [refreshProjects]);

  const handleToggleMonitoring = async (areaId: string) => {
    const newState = !monitoringStates[areaId];

    try {
      // Set toggling state to show loading spinner
      setTogglingStates(prev => ({ ...prev, [areaId]: true }));

      // Call the API to toggle monitoring
      const response = await realtimeApi.toggleMonitoring({
        map_id: areaId,
        enable: newState
      });

      // Update the monitoring state based on API response
      setMonitoringStates(prev => ({
        ...prev,
        [areaId]: response.is_monitored
      }));
    } catch (err) {
      console.error('Failed to toggle monitoring:', err);
      alert(`Failed to ${newState ? 'enable' : 'disable'} monitoring: ${err instanceof Error ? err.message : 'Unknown error'}`);

      // Revert the toggle state on error
      setMonitoringStates(prev => ({
        ...prev,
        [areaId]: !newState
      }));
    } finally {
      setTogglingStates(prev => ({ ...prev, [areaId]: false }));
    }
  };

  const toggleSelected = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  // Pre-fill a project name from a shared name prefix (e.g. "ie_…" -> "ie").
  const deriveName = (fallback: string): string => {
    const names = savedAreas.filter((a) => selectedIds.has(a.id)).map((a) => a.name);
    let prefix = names[0] || '';
    for (const n of names) {
      while (prefix && !n.startsWith(prefix)) prefix = prefix.slice(0, -1);
    }
    prefix = prefix.replace(/[_\s-]+$/, '');
    return prefix.length >= 2 ? prefix : fallback;
  };

  const previewSelection = () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    navigate(
      `${productPath(scope, 'project')}?ids=${ids.map(encodeURIComponent).join(',')}&name=${encodeURIComponent(deriveName('Selection'))}`
    );
  };

  const openSaveModal = () => {
    if (!selectedIds.size) return;
    setProjectName(deriveName('New project'));
    setNameModalOpen(true);
  };

  const confirmSaveProject = async () => {
    const ids = Array.from(selectedIds);
    const name = projectName.trim();
    if (!ids.length || !name) return;
    try {
      setSavingProject(true);
      const project = await projectsApi.create({ name, areaIds: ids });
      console.log('✅ Saved project', project.id, project.name, `(${ids.length} areas)`);
      setNameModalOpen(false);
      clearSelection();
      setNotice({ text: `Saved ${pluralAreas(ids.length)} to ${project.name}`, projectId: project.id });
      await refreshProjects();
    } catch (err) {
      console.error('❌ Failed to save project:', err);
    } finally {
      setSavingProject(false);
    }
  };

  // Adds the ticked areas to a project; any already in it are skipped.
  const addToProject = async (p: Project) => {
    const fresh = Array.from(selectedIds).filter((id) => !p.areaIds.includes(id));
    const already = selectedIds.size - fresh.length;
    try {
      setSavingProject(true);
      if (fresh.length) await projectsApi.addAreas(p.id, fresh);
      clearSelection();
      const added = fresh.length ? `Added ${pluralAreas(fresh.length)} to ${p.name}` : `Nothing added to ${p.name}`;
      setNotice({ text: already ? `${added} · ${already} already in it` : added, projectId: p.id });
      await refreshProjects();
    } catch (err) {
      console.error('❌ Failed to add areas to project:', err);
    } finally {
      setSavingProject(false);
    }
  };

  const removeProject = async (id: string) => {
    try {
      await projectsApi.remove(id);
      await refreshProjects();
    } catch (err) {
      console.error('Failed to remove project:', err);
    }
  };

  // --- bulk "enable/disable all" monitoring for a project's custom polygons ---
  const monitoredSet = useMemo(
    () => new Set(Object.keys(monitoringStates).filter((id) => monitoringStates[id])),
    [monitoringStates]
  );

  // Projects live in the shared store; show only those relevant to THIS env — a
  // member area whose realtime data is readable here — inferred from bqProjectId.
  const visibleProjects = useMemo(() => {
    const bqById = new Map(savedAreas.map((a) => [a.id, a.bqProjectId]));
    return projects.filter((p) => projectInThisEnv(p.areaIds, bqById));
  }, [projects, savedAreas]);

  const applyMonitoring = (ids: string[], enable: boolean) =>
    setMonitoringStates((prev) => {
      const next = { ...prev };
      ids.forEach((id) => (next[id] = enable));
      return next;
    });

  const enableAllInProject = async (p: Project) => {
    const toEnable = monitorableMembers(p.areaIds, savedAreas, monitoredSet)
      .filter((m) => !m.monitored)
      .map((m) => m.id);
    if (!toEnable.length) return;
    setMonitorBusy(true);
    await bulkSetMonitoring(toEnable, true);
    applyMonitoring(toEnable, true);
    setMonitorBusy(false);
  };

  const requestDisableAllInProject = (p: Project) => {
    const active = monitorableMembers(p.areaIds, savedAreas, monitoredSet).filter((m) => m.monitored);
    if (!active.length) return;
    setConfirmDisable({ project: p, shared: sharedMembers(active, projects, p.id) });
  };

  const confirmDisableAll = async () => {
    if (!confirmDisable) return;
    const toDisable = monitorableMembers(confirmDisable.project.areaIds, savedAreas, monitoredSet)
      .filter((m) => m.monitored)
      .map((m) => m.id);
    setMonitorBusy(true);
    await bulkSetMonitoring(toDisable, false);
    applyMonitoring(toDisable, false);
    setMonitorBusy(false);
    setConfirmDisable(null);
  };

  const renderRowExtras = (area: SavedArea, state: RowState): RowExtras => {
    const dataCount = dataCountFor(area);
    return {
      body: (
        <>
          {state.supported && dataCount > 0 ? (
            <div className="badge badge-success badge-sm gap-1 mb-2">live · {dataCount.toLocaleString()}</div>
          ) : null}
          <AreaIdBadges area={area} />
        </>
      ),
      footer: (
        <>
          <div className="flex items-center justify-between mt-2 pt-4 border-t border-base-300">
            <span className="text-sm font-medium">Live Monitoring</span>
            <div className="flex items-center gap-2">
              {togglingStates[area.id] && <span className="loading loading-spinner loading-sm"></span>}
              {readOnly ? (
                <span className="text-sm text-base-content/70">{monitoringStates[area.id] ? 'On' : 'Off'}</span>
              ) : (
                <input
                  type="checkbox"
                  className="toggle toggle-primary"
                  checked={monitoringStates[area.id] || false}
                  onChange={() => handleToggleMonitoring(area.id)}
                  disabled={togglingStates[area.id]}
                />
              )}
            </div>
          </div>
          <button
            className="btn btn-sm btn-outline mt-3 w-full"
            onClick={() =>
              navigate(`${productPath(scope, 'dashboard')}?id=${encodeURIComponent(area.id)}&name=${encodeURIComponent(area.name)}`)
            }
          >
            View Dashboard
          </button>
        </>
      ),
    };
  };

  // What is ticked in select mode, in the shape the picker takes.
  const selection = useMemo(
    () => savedAreas.filter((a) => selectedIds.has(a.id)).map((a) => toSelectedArea(a)),
    [savedAreas, selectedIds],
  );

  return (
    <div className="min-h-screen bg-base-200">
      <NavBar title={scopeTitle(scope, "Monitor Areas Select")} showBackButton={true} />

      <div className={`w-full max-w-6xl mx-auto px-4 mt-8 ${selectedIds.size || notice ? 'pb-28' : 'pb-12'}`}>
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold">{scopeTitle(scope, "Monitor Areas")}</h2>
          <p className="text-lg text-base-content/70 mt-2">
            {scopeName(scope, 'Real Time')} supports custom polygons only
          </p>
          {!readOnly && (
            <p className="text-sm text-base-content/60 mt-2">Tick “Add to project” on areas to group them into a project.</p>
          )}
        </div>

        {!loading && visibleProjects.length > 0 && (
          <div className="mb-8">
            <h3 className="mb-2 text-sm font-semibold text-base-content/70">Your projects</h3>
            <ProjectMonitorCards
              projects={visibleProjects}
              areas={savedAreas}
              monitoredSet={monitoredSet}
              busy={monitorBusy}
              onOpen={(p) => navigate(`${productPath(scope, 'project')}?project=${encodeURIComponent(p.id)}`)}
              onDelete={removeProject}
              onEnableAll={enableAllInProject}
              onRequestDisableAll={requestDisableAllInProject}
              readOnly={readOnly}
            />
          </div>
        )}

        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="loading loading-spinner loading-lg"></div>
            <span className="ml-4 text-lg">Loading saved areas...</span>
          </div>
        ) : error ? (
          <div className="alert alert-error max-w-2xl mx-auto">
            <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>{error}</span>
          </div>
        ) : savedAreas.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-lg text-base-content/70">No saved areas found</p>
            <p className="text-sm text-base-content/50 mt-2">Create and save a custom polygon to monitor it here</p>
          </div>
        ) : (
          <AreaPicker
            product="realtime"
            mode={readOnly ? 'single' : 'multi'}
            selection={selection}
            onChange={readOnly ? undefined : (area) => toggleSelected(area.id)}
            selectLabel="Add to project"
            selectBy="checkbox"
            showCounts={false}
            groups={[
              { id: 'monitored', title: 'Monitored polygons', areas: monitoredAreas, quietTitle: true },
              { id: 'other', title: 'Other polygons', areas: otherAreas, quietTitle: true },
            ]}
            hiddenNote={hiddenNote}
            renderRowExtras={renderRowExtras}
          />
        )}
      </div>

      {selectedIds.size > 0 ? (
        <div className="fixed inset-x-0 bottom-0 z-40 border-t border-base-300 bg-base-100/95 px-4 py-3 shadow-lg backdrop-blur">
          <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-2">
            <span className="text-sm font-medium">{selectedIds.size} selected</span>
            <div className="flex flex-wrap gap-2">
              <button className="btn btn-sm btn-ghost" onClick={clearSelection}>
                Clear
              </button>
              <button className="btn btn-sm btn-outline" onClick={previewSelection}>
                Preview
              </button>
              {visibleProjects.length > 0 && (
                <div className="dropdown dropdown-top dropdown-end">
                  <button tabIndex={0} className="btn btn-sm btn-outline" disabled={savingProject}>
                    Add to existing project ▾
                  </button>
                  <ul
                    tabIndex={0}
                    aria-label="Projects"
                    className="dropdown-content menu z-50 mb-2 max-h-72 w-64 flex-nowrap overflow-y-auto rounded-box bg-base-100 p-2 shadow-lg"
                  >
                    {visibleProjects.map((p) => (
                      <li key={p.id}>
                        <button onClick={() => addToProject(p)}>{p.name}</button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <button className="btn btn-sm btn-primary" onClick={openSaveModal} disabled={savingProject}>
                Save as project
              </button>
            </div>
          </div>
        </div>
      ) : notice ? (
        <div role="status" className="fixed inset-x-0 bottom-0 z-40 border-t border-base-300 bg-base-100/95 px-4 py-3 shadow-lg backdrop-blur">
          <div className="mx-auto flex max-w-6xl items-center justify-between gap-2">
            <span className="text-sm font-medium">{notice.text}</span>
            <div className="flex gap-2">
              <button
                className="btn btn-sm btn-primary"
                onClick={() => navigate(`${productPath(scope, 'project')}?project=${encodeURIComponent(notice.projectId)}`)}
              >
                Open
              </button>
              <button className="btn btn-sm btn-ghost" onClick={() => setNotice(null)} aria-label="Dismiss">
                ✕
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {nameModalOpen && (
        <div className="modal modal-open">
          <div className="modal-box">
            <h3 className="text-lg font-bold">Save as project</h3>
            <p className="mt-1 text-sm text-base-content/70">
              {selectedIds.size} area{selectedIds.size === 1 ? '' : 's'} · grouped for dashboards
            </p>
            <input
              className="input input-bordered mt-4 w-full"
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              placeholder="Project name"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') confirmSaveProject();
              }}
            />
            <div className="modal-action">
              <button className="btn btn-ghost" onClick={() => setNameModalOpen(false)}>
                Cancel
              </button>
              <button
                className="btn btn-primary"
                disabled={!projectName.trim() || savingProject}
                onClick={confirmSaveProject}
              >
                {savingProject ? 'Saving…' : 'Save project'}
              </button>
            </div>
          </div>
          <div className="modal-backdrop" onClick={() => setNameModalOpen(false)} />
        </div>
      )}

      <ConfirmDialog
        open={confirmDisable !== null}
        title="Disable monitoring for all areas?"
        danger
        busy={monitorBusy}
        confirmLabel="Disable all"
        onConfirm={confirmDisableAll}
        onCancel={() => setConfirmDisable(null)}
        body={
          <>
            <p>
              This stops live monitoring for every monitored area in{' '}
              <span className="font-medium text-ink">{confirmDisable?.project.name}</span>.
            </p>
            {confirmDisable && <SharedProjectsWarning shared={confirmDisable.shared} />}
          </>
        }
      />
    </div>
  );
};

export default MonitorAreasSelect;
